import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Checkbox, IconButton, Stack, TableCell, TableRow, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { JSXElementConstructor, ReactElement, useEffect, useRef, useState } from "react";
import { useUserProfileStore } from "../../../State/Stores/UserProfileStore";
import {
  ICandidateEmailAddress,
  ICandidateLocation,
  ICandidatePhoneNumber,
} from "../../../Typings/Candidate";
import { ITableColumn } from "../../../Typings/TableColumn";
import createSlug from "../../../Utilities/createSlug";
import { formatPhone } from "../../../Utilities/formatUtilities";

export interface ICustomRowProps {
  columns: ITableColumn[];
  isEditing?: boolean;
  row?: any;
  rowChecked?: boolean;
  handleRowClick?: (row: any, select: boolean, e: any) => void;
  title?: string;
  customId?: string;
  renderCustomCell?: (
    row: any,
    column: ITableColumn,
    adjustDate: (date: Date | string | Dayjs) => Dayjs,
  ) => React.ReactNode;
  rowDisabled?: boolean;
  disabledRowTooltip?: string;
  disableTruncation?: boolean;
  hover?: boolean;
  timezoneOffset?: string;
}

export function CustomTableRow(props: ICustomRowProps) {
  return props.rowDisabled ? (
    <Tooltip title={props.disabledRowTooltip ?? ""} placement="bottom-start">
      {SingleTableRow(props)}
    </Tooltip>
  ) : (
    SingleTableRow(props)
  );
}

function SingleTableRow({
  columns = [],
  isEditing = false,
  row = {},
  rowChecked = false,
  handleRowClick = () => {
    return;
  },
  title,
  renderCustomCell,
  rowDisabled = false,
  customId,
  disableTruncation = false,
  hover = true,
  timezoneOffset,
}: ICustomRowProps) {
  const id = customId ?? (row.id || row.candidateId);
  const { getUserAdjustedDate } = useUserProfileStore();

  return (
    <TableRow
      hover={hover}
      title={title}
      data-test={`${title ? createSlug(title) : "candidate"}-${id}`}
      onClick={(event) => {
        if (!rowDisabled) {
          event.stopPropagation();
          handleRowClick(row, false, event);
        }
      }}
      aria-checked={rowChecked}
      selected={rowChecked}
      sx={(theme) => ({
        cursor: "pointer",
        textDecoration: "none",
        "&:hover": hover
          ? {
              background: `${theme.colors.secondary.fade_Dark} !important`,
            }
          : {
              cursor: "default",
            },
        "&:nth-of-type(odd)": {
          background: theme.colors.secondary.fade_Light,
        },
        "&.Mui-selected": {
          background: theme.colors.secondary.fade_Light,
          "&:nth-of-type(odd)": {
            background: theme.colors.secondary.fade,
          },
          "&:hover": hover
            ? {
                background: `${theme.colors.secondary.fade_Dark} !important`,
              }
            : {
                cursor: "default",
              },
        },
      })}
    >
      {isEditing ? (
        <TableCell align="left" id={`item-selection-${id}`} data-test="table-edit-checkbox">
          <Checkbox
            disabled={rowDisabled}
            size="small"
            id={`item-selection-${id}`}
            data-test={`table-edit-checkbox-${id}`}
            checked={rowChecked}
            onClick={(event) => {
              if (!rowDisabled) {
                event.stopPropagation();
                handleRowClick(row, !rowChecked, event);
              }
            }}
            inputProps={{ "aria-labelledby": `item-${id}` }}
          ></Checkbox>
        </TableCell>
      ) : null}
      {columns.map((column: ITableColumn, elementIndex: number) => {
        return (
          <TableCell
            align="left"
            id={elementIndex === 0 ? `item-${id}` : `item-${column.propName}-${id}`}
            key={`item-${column.propName}-${id}`}
            data-test={`item-${column.propName}-${id}`}
          >
            {column.propName !== "actions" && !disableTruncation ? (
              <Cell columnCount={columns.length}>
                {renderCustomCell
                  ? renderCustomCell(row, column, getUserAdjustedDate)
                  : defaultCellValue(row, column, getUserAdjustedDate)}
              </Cell>
            ) : (
              <>
                {renderCustomCell
                  ? renderCustomCell(row, column, getUserAdjustedDate)
                  : defaultCellValue(row, column, getUserAdjustedDate)}
              </>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

function Cell({
  columnCount,
  children,
}: {
  columnCount: number;
  children: ReactElement<any, string | JSXElementConstructor<any>>;
}) {
  const [expand, setExpand] = useState<boolean>(false);
  const [isOverFlown, setIsOverFlown] = useState<boolean>(false);
  const elementRef = useRef<HTMLDivElement>();
  const maxWidth = getWidthForColumnCount(columnCount);

  useEffect(() => {
    if (elementRef && elementRef.current) {
      setIsOverFlown(elementRef.current.scrollWidth > elementRef.current.clientWidth);
    }
  }, []);

  return (
    <Stack direction={"row"} sx={{ maxWidth: maxWidth, wordBreak: "break-word" }}>
      {!expand ? (
        <>
          <Box
            ref={elementRef}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              position: "relative",
              textOverflow: isOverFlown ? "ellipsis" : "initial",
            }}
          >
            {children}
          </Box>
          {isOverFlown && (
            <Stack direction={"column"} justifyContent="center">
              <IconButton
                onClick={(ev: any) => {
                  ev.stopPropagation();
                  ev.preventDefault();
                  setExpand(!expand);
                }}
                sx={{ fontSize: 13, mt: -0.5, ml: 1 }}
              >
                <ExpandMore fontSize="inherit" />
              </IconButton>
            </Stack>
          )}
        </>
      ) : (
        <>
          <Box sx={{ width: "auto", maxHeight: "auto" }}>{children}</Box>
          {isOverFlown && (
            <Stack direction={"column"} justifyContent="center">
              <IconButton
                onClick={(ev: any) => {
                  ev.stopPropagation();
                  ev.preventDefault();
                  setExpand(!expand);
                }}
                sx={{ fontSize: 13, mt: -0.5, ml: 1 }}
              >
                <ExpandLess fontSize="inherit" />
              </IconButton>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}

const getWidthForColumnCount = (columnCount: number) => {
  if (columnCount <= 4) return 350;
  if (columnCount <= 6) return 250;
  return 180;
};

export const defaultCellValue = (
  row: any,
  column: ITableColumn,
  adjustDate: (date: Date | string | Dayjs) => Dayjs,
) => {
  let value = row[column.propName];

  if (value) {
    const isTime =
      value &&
      dayjs(value).isValid() &&
      (column.propName.toLowerCase().includes("date") || column.propName.toLowerCase().includes("time"));
    if (isTime) {
      value = adjustDate(value).format("M/D/YYYY h:mm A");
    } else if (column.propName === "tags") {
      value = value && Array.isArray(value) ? value.map((tag: string) => tag).join(", ") : "";
    } else if (column.propName.indexOf("emailAddress") >= 0) {
      value =
        value && Array.isArray(value)
          ? value.map((email: ICandidateEmailAddress) => email.emailAddress).join(", ")
          : "";
    } else if (column.propName.indexOf("phoneNumbers") >= 0) {
      value =
        value && Array.isArray(value)
          ? value.map((phone: ICandidatePhoneNumber) => formatPhone(phone.phoneNumber)).join(", ")
          : "";
    } else if (column.propName.indexOf("locations") >= 0) {
      value =
        value && Array.isArray(value)
          ? value.map((location: ICandidateLocation) => location.name).join(", ")
          : "";
    }
    return value;
  }

  return "";
};
